import React, { useMemo } from "react";
import TspLoadError from "../../components/common/TspLoadError";
import useTspDashboardData from "./useTspDashboardData";
import * as model from "./dashboardModel";
import {
  DashboardHeading,
  StatCardsRow,
  BookDistributionOverview,
  DistributionByMethod,
  LiveTicker,
  BookWiseChart,
  YearWiseBookDistributionChart,
  VisitWiseChart,
  ActiveJourneysCard,
  PublicResponseScoreCard,
  StateWiseDistributionTable,
  JourneyLocationsMap,
  RecentActivitiesStrip,
} from "./sections";

// GET /api/tsp/dashboard, scoped server-side to the caller's own teams (or
// every team with `journeys.approve` oversight). `dashboardModel.js` maps the
// response onto each section's existing props; sections gated on their own
// permission (journey map, recent media) arrive as `null` and render their own
// "no access" line rather than being skipped.
export default function TspDashboardPage() {
  const { data, loading, error, reload } = useTspDashboardData();
  // Memoised on the map data, so the map redraws its markers only when a
  // refresh delivers it, not on every re-render in between.
  const journeyMap = data ? data.journey_map : null;
  const journeyLocations = useMemo(() => model.buildJourneyLocations(journeyMap), [journeyMap]);

  if (error) return <TspLoadError error={error} onRetry={reload} what="the TSP dashboard" />;
  if (loading || !data) return <div className="tsp-page tsp-dashboard-loading">Loading dashboard…</div>;

  const bookWise = model.buildBookWiseChart(data.book_wise);

  return (
    <div className="tsp-page tsp-dashboard">
      <DashboardHeading banner={model.buildActiveJourneyBanner(data.active_journey)} />
      <StatCardsRow stats={model.buildStatCards(data.stats)} />

      <div className="tsp-grid tsp-grid-2-1">
        <BookDistributionOverview overview={model.buildBookDistributionOverview(data.distribution_overview, data.stats)} />
        <DistributionByMethod data={model.buildDistributionByMethod(data.by_method)} total={data.stats.books_distributed.toLocaleString("en-IN")} />
      </div>

      <LiveTicker items={model.buildLiveTicker(data.live_ticker)} />

      <div className="tsp-grid tsp-grid-3 tsp-dashboard-charts">
        <VisitWiseChart data={model.buildVisitWise(data.visit_wise)} />
        <BookWiseChart data={bookWise.data} series={bookWise.series} />
        <YearWiseBookDistributionChart data={model.buildYearWiseDistribution(data.year_wise)} />
      </div>

      <div className="tsp-grid tsp-grid-2">
        <ActiveJourneysCard journeys={model.buildActiveJourneys(data.active_journeys)} />
        <PublicResponseScoreCard score={model.buildPublicResponseScore(data.public_response)} />
      </div>

      <div className="tsp-grid tsp-grid-2 tsp-dashboard-lower">
        <StateWiseDistributionTable stateWise={model.buildStateWise(data.state_wise)} />
        <JourneyLocationsMap locations={journeyLocations} />
      </div>

      <RecentActivitiesStrip items={model.buildRecentActivities(data.recent_activities)} />
    </div>
  );
}
